import type { Experience, Experiences } from "../experiencesSchema";

export function parseExperiencesHeader(
  experiencesHeader: string,
  // We should only alert when called server side
  onError?: (message: string, data?: unknown) => void,
): Experiences {
  try {
    if (!experiencesHeader) return [];

    const parsedData = JSON.parse(base64Decode(experiencesHeader));
    if (!Array.isArray(parsedData)) return [];

    const badExperiences: Experiences = [];
    const filteredExperiences = parsedData.filter((item) => {
      const isValid = isValidExperience(item);
      if (!isValid) badExperiences.push(item);
      return isValid;
    });

    if (badExperiences.length > 0) {
      onError?.("Some experiences failed validation", {
        badExperiences,
      });
    }

    return filteredExperiences;
  } catch (error) {
    onError?.("Failed to parse experiences header:", error);
    return [];
  }
}

/* #####################  */
/* Zod's use of BigInt makes it unsupported in Safari 12/13. */
/* Once we drop support for those, we can switch back to the schema commented out in experiencesSchema. */
/* #####################  */

const isString = (val: unknown): val is string => typeof val === "string";
const isOptionalString = (val: unknown): val is string | undefined =>
  val === undefined || typeof val === "string";
const isNumber = (val: unknown): val is number => typeof val === "number";
const isObject = (val: unknown): val is Record<string, unknown> =>
  typeof val === "object" && val !== null && !Array.isArray(val);
const isNullOrUndefined = (val: unknown): val is null | undefined =>
  val === null || val === undefined;

const isValidExperience = (val: unknown): val is Experience => {
  if (!isObject(val)) return false;

  const {
    experienceName,
    experienceId,
    variantLabel,
    parameters,
    impressionId,
    component,
  } = val;

  return (
    isString(experienceName) &&
    (isString(experienceId) || isNumber(experienceId)) &&
    isOptionalString(variantLabel) &&
    (isNullOrUndefined(parameters) || isObject(parameters)) &&
    (isNullOrUndefined(impressionId) || isString(impressionId)) &&
    (isNullOrUndefined(component) || isString(component))
  );
};

function base64Decode(input: string) {
  return Buffer.from(input, "base64").toString("utf8");
}
